<template>
  <section
    class="py-5 lg:py-6 overflow-hidden"
    :class="{ 'mb-6': isAdventCalendar }"
  >
    <div class="container">
      <h2 class="mb-5 lg:mb-6 text-center">
        {{ isAdventCalendar ? t('more_about_calendar') : t('read_more_articles') }}
      </h2>
      <div class="row no-gutters ml-n2 md:ml-n3 lg:ml-n4 xl:ml-n5 xxl:ml-n7">
        <!-- Mobile -->
        <Swiper
          v-if="BrowserService.isTablet()"
          id="js-similar-blog-posts"
          class="lightSlider--center row no-gutters mx-0 w-100 mb-0 flex-nowrap flex-grow-1 h-auto list-unstyled"
          slides-per-view="auto"
          :scrollbar="{ draggable: true, hide: false }"
        >
          <!-- blog item -->
          <SwiperSlide
            v-for="article in articles"
            :key="article.id"
            class="col-11 sm:col-8 md:col-6 lg:col-4 flex flex-col pl-2 md:pl-3 lg:pl-4 xl:pl-5 xxl:pl-7"
          >
            <article class="shadowbox p-3 sm:p-4 flex flex-col flex-grow-1 relative rounded-lg">
              <div class="embed-responsive embed-responsive-4by3 mb-4">
                <RemoteResponsiveImage
                  :product-image-data="article.image"
                  img-class="embed-responsive-item image-cover"
                  :alt="article.title"
                  sizes="(min-width: 1200px) 25vw, (min-width: 992px) 20vw, (min-width: 600px) 35vw, (min-width: 500px) 50vw, 70vw"
                />
              </div>

              <h3 class="mb-4 leading-base text-lg font-family-base font-medium">
                <router-link
                  :to="{ name: 'showBlogPath', params: { permalink: article.permalink } }"
                  class="stretched-link no-decoration"
                >
                  {{ article.title }}
                </router-link>
              </h3>
              <p class="mb-5 text-sm">
                {{ article.perex }}
              </p>
              <div class="mt-auto text-center">
                <router-link
                  :to="{ name: 'showBlogPath', params: { permalink: article.permalink } }"
                  class="btn btn-outline-primary px-5 stretched-link rounded-pill font-medium"
                >
                  {{ t('read_full_article') }}
                </router-link>
              </div>
            </article>
          </SwiperSlide>
        </Swiper>
        <!-- Desktop -->
        <ul
          v-else
          class="row mx-0 w-100 mb-0 flex-nowrap lg:flex-wrap flex-grow-1 h-auto no-gutters mb-0 list-unstyled"
        >
          <li
            v-for="article in articles"
            :key="article.id"
            class="col-6 sm:col-5 md:col-4 flex flex-col pl-2 md:pl-3 lg:pt-3 xl:pl-4 xl:pt-4"
          >
            <article class="shadowbox p-3 sm:p-4 flex flex-col flex-grow-1 relative rounded-lg">
              <RemoteResponsiveImage
                :product-image-data="article.image"
                img-class="w-100 mb-4"
                :alt="article.title"
                sizes="(min-width: 1200px) 25vw, (min-width: 992px) 20vw, (min-width: 600px) 35vw, (min-width: 500px) 50vw, 70vw"
              />

              <h3 class="mb-4 leading-base text-lg font-family-base font-medium">
                <router-link
                  :to="{ name: 'showBlogPath', params: { permalink: article.permalink } }"
                  class="stretched-link no-decoration"
                >
                  {{ article.title }}
                </router-link>
              </h3>
              <p class="mb-5 text-sm">
                {{ article.perex }}
              </p>
              <div class="mt-auto text-center">
                <router-link
                  :to="{ name: 'showBlogPath', params: { permalink: article.permalink } }"
                  class="btn btn-outline-primary px-5 stretched-link rounded-pill font-medium"
                >
                  {{ t('read_full_article') }}
                </router-link>
              </div>
            </article>
          </li>
        </ul>
      </div>
      <div
        v-if="!isAdventCalendar"
        class="mt-7 text-center"
      >
        <router-link
          :to="{ name: 'blogPath' }"
          class="btn btn-success  px-5 py-3 rounded-pill font-medium"
        >
          {{ t('show_all') }}
        </router-link>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import SwiperCore from 'swiper'
import { Scrollbar } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import RetailApiV2 from '@/api/retailApi/RetailApiV2'
import RemoteResponsiveImage from '@/components/general/BaseRemoteResponsiveImage.vue'
import BrowserService from '@/services/browserService'
import type { Article, Articles } from '@/types/articles'

SwiperCore.use([Scrollbar])

const props = defineProps({
  isAdventCalendar: {
    type: Boolean,
    default: false
  }
});

const { t } = useI18n()
const articles = ref<Article[]>([])
const swiper = ref()

const adventArticles = [
  t('advent_article_1'),
  t('advent_article_2'),
  t('advent_article_3'),
];

const initPage = async () => {
  if (!props.isAdventCalendar) {
    const { data } = await RetailApiV2.get('articles?limit=3')
    articles.value = (data as Articles).articles
  } else {
    articles.value.push(
      ...(await Promise.all(
        adventArticles.map(async (article) => (await RetailApiV2.get(`articles/preview/${article}`)).data.article),
      )),
    );
  }

  if (BrowserService.isTablet()) {
    // @ts-expect-error we know #js-similar-blog-posts is a swiper
    swiper.value = document.querySelector('#js-similar-blog-posts').swiper
  }
  if (swiper.value) {
    setTimeout(() => {
      swiper.value.scrollbar?.updateSize()
    }, 1000)
  }
}
initPage()
</script>

<i18n>
  {
    "cs": {
    "advent_article_1": "adventni-kalendar-mixit",
    "advent_article_2": "15-duvodu-proc-adventni-kalendar",
    "advent_article_3": "velky-adventni-kalendar",
    "more_about_calendar": "Více o adventním kalendáři",
    "read_full_article": "Číst celý článek",
    "read_more_articles": "Číst další články",
    "show_all": "Zobrazit všechny"
},
    "sk": {
    "advent_article_1": "adventny-kalendar-2025",
    "advent_article_2": "15-dovodov-preco-adventny-kalendar",
    "advent_article_3": "adventny-kalendar-mixit",
    "more_about_calendar": "Viac o adventnom kalendári",
    "read_full_article": "Čítať celý článok",
    "read_more_articles": "Čítať ďalšie články",
    "show_all": "Zobraziť všetky"
},
    "pl": {
    "advent_article_1": "kalendarz-adwentowy-mixit",
    "advent_article_2": "15_powodow",
    "advent_article_3": "kalendarz2025",
    "more_about_calendar": "Więcej o kalendarzu adwentowym",
    "read_full_article": "Przeczytaj cały artykuł",
    "read_more_articles": "Zobacz inne artykuły",
    "show_all": "Zobacz wszystkie"
},
    "hu": {
    "advent_article_1": "adventi-naptar-tortenete",
    "advent_article_2": "15_ok_advetni_naptar_mellett",
    "advent_article_3": "adventi_naptar_2025",
    "more_about_calendar": "Többet az adventi naptárról",
    "read_full_article": "Elolvasom a bejegyzést",
    "read_more_articles": "Következő bejegyzés",
    "show_all": "Mutass mindet"
},
    "de": {
    "advent_article_1": "adventskalender",
    "advent_article_2": "15-gruende-adventskalender",
    "advent_article_3": "adventskalender-2025",
    "more_about_calendar": "Mehr zum Adventskalender",
    "read_full_article": "Den ganzen Artikel lesen",
    "read_more_articles": "Weitere Artikel lesen",
    "show_all": "Alle anzeigen"
},
    "at": {
    "advent_article_1": "adventskalender",
    "advent_article_2": "15-gruende-adventskalender",
    "advent_article_3": "adventskalender-2025",
    "more_about_calendar": "Mehr zum Adventskalender",
    "read_full_article": "Den ganzen Artikel lesen",
    "read_more_articles": "Weitere Artikel lesen",
    "show_all": "Alle anzeigen"
},
    "ro": {
    "advent_article_1": "calendar-advent-mixit",
    "advent_article_2": "15-motive-pentru-calendarul-advent-mixit",
    "advent_article_3": "marele-calendar-advent-mixit-2025",
    "more_about_calendar": "Mai multe despre calendarul de Advent",
    "read_full_article": "Citesc toată postarea",
    "read_more_articles": "Următoarea postare",
    "show_all": "Arată toate"
}
  }
</i18n>
